import { useState, useMemo, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { LanguageContext, UserContext } from '../../../../Context';
import { net } from '../../../../database';
import useDBO from '../../hooks/useDBO';
import Lang from '../../components/Lang';
import PremiumButton from '../../components/PremiumButton';
import { Plus, CheckCircle, Circle, Edit2, Trash2, X, Save, BookOpen, Link as LinkIcon, Youtube, Image as ImageIcon, Upload, ExternalLink, FolderOpen } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensors, useSensor } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/**
 * Sortable category button used in the horizontal category navigation bar.
 * Supports drag-and-drop reordering via @dnd-kit.
 * @date 4/7/2026
 * @version 1.0.0
 * @author Forge
 *
 * @param {Object} props
 * @param {Object} props.category - Category object with _id, title_vi, title_en
 * @param {string} props.activeCategory - The currently active (highlighted) category ID
 * @param {function} props.onClick - Click handler for scrolling to the category section
 * @returns {React.ReactElement}
 */
function SortableCategoryButton({ category, activeCategory, onClick }) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: category._id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 20 : 'auto',
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <button
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            onClick={onClick}
            className={`flex flex-col items-center justify-center whitespace-nowrap px-5 py-2 rounded-lg text-sm transition-all touch-none select-none cursor-pointer ${activeCategory === category._id ? 'bg-teal-700 text-white shadow-lg shadow-teal-700/30' : 'bg-slate-50 text-slate-500 hover:text-teal-700 hover:bg-teal-50 border border-slate-200 hover:border-teal-300'}`}
        >
            <span className="font-bold">{category.title_vi}</span>
            <span className={`text-[10px] uppercase tracking-wider ${activeCategory === category._id ? 'text-teal-100' : 'text-slate-400'}`}>{category.title_en}</span>
        </button>
    );
}

/** @type {{ _id?: string, title_vi: string, title_en: string, content_vi: string, content_en: string, section: string, category: string, roles: string[], officialProcessLink?: string, appLink?: string, videoUrl?: string, imageUrls?: string[] }} */
const DEFAULT_FORM = {
    _id: undefined,
    title_vi: '',
    title_en: '',
    content_vi: '',
    content_en: '',
    section: 'General',
    category: '',
    roles: [],
    officialProcessLink: '',
    appLink: '',
    videoUrl: '',
    imageUrls: []
};

/**
 * Extracts the YouTube video ID from a URL string.
 * @date 4/7/2026
 * @version 1.0.0
 * @author Forge
 *
 * @param {string} url - YouTube URL
 * @returns {string|null} The 11-character video ID, or null if not found
 */
function getYouTubeId(url) {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
}

/**
 * Card component for displaying a single manual item with bilingual content,
 * media embeds, acknowledgement button, and manager actions.
 * @date 4/7/2026
 * @version 1.0.0
 * @author Forge
 *
 * @param {Object} props
 * @param {Object} props.item - The manual item data
 * @param {boolean} props.isManager - Whether the current user has manager privileges
 * @param {function} props.onEdit - Handler for editing the item
 * @param {function} props.onDelete - Handler for deleting the item
 * @param {function} props.onAcknowledge - Handler for acknowledging the item
 * @returns {React.ReactElement}
 */
function ManualItemCard({ item, isManager, onEdit, onDelete, onAcknowledge }) {
    const { l, language } = useContext(LanguageContext);
    const [ackPending, setAckPending] = useState(false);
    const navigate = useNavigate();

    const showTopActions = isManager || item.officialProcessLink || item.appLink;

    /**
     * Helper to get a translation from lang.json.
     * @param {string} key - The translation key
     * @returns {string} The translated string for the current language
     */
    const t = (key) => {
        try {
            return l[key][language];
        } catch {
            return key;
        }
    };

    return (
        <div className="border border-slate-200 rounded-xl p-6 bg-white hover:shadow-lg hover:shadow-teal-700/5 transition-all duration-300 group relative ml-2">
            <div className="flex items-start gap-6">
                <div className="flex-1 space-y-4">
                    <div className="space-y-2">
                        <h3 className="text-lg font-bold text-slate-800">{item.title_vi}</h3>
                        <div className="text-slate-700 whitespace-pre-line leading-relaxed">{item.content_vi}</div>
                    </div>
                    <div className="h-px bg-gradient-to-r from-teal-200 via-slate-200 to-transparent w-full" />
                    <div className="space-y-2 opacity-75">
                        <h4 className="text-base font-semibold text-slate-500">{item.title_en}</h4>
                        <div className="text-slate-500 text-sm whitespace-pre-line leading-relaxed">{item.content_en}</div>
                    </div>

                    {/* Media Inline */}
                    {((item.videoUrl && getYouTubeId(item.videoUrl)) || (item.imageUrls && item.imageUrls.length > 0)) && (
                        <div className="pt-4 space-y-4">
                            {item.videoUrl && getYouTubeId(item.videoUrl) && (
                                <div className="rounded-xl overflow-hidden aspect-video bg-slate-100 max-w-lg shadow-md border border-slate-200">
                                    <iframe
                                        width="100%"
                                        height="100%"
                                        src={`https://www.youtube.com/embed/${getYouTubeId(item.videoUrl)}`}
                                        title="YouTube video player"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        className="border-0"
                                    ></iframe>
                                </div>
                            )}

                            {item.imageUrls && item.imageUrls.length > 0 && (
                                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-brand-border scrollbar-track-transparent">
                                    {item.imageUrls.map((url, idx) => (
                                        <a
                                            key={idx}
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 w-32 h-32 rounded-lg overflow-hidden border border-slate-200 hover:opacity-90 hover:shadow-md transition-all bg-white shadow-sm"
                                        >
                                            <img src={url} alt={`Attachment ${idx + 1}`} className="w-full h-full object-cover" />
                                        </a>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {isManager && item.roles && item.roles.length > 0 && (
                        <div className="pt-2">
                            <span className="text-xs bg-teal-50 text-teal-700 px-2.5 py-1 rounded-md border border-teal-200 font-medium">
                                {t('manual.visibleTo')}: {item.roles.join(', ')}
                            </span>
                        </div>
                    )}
                </div>

                {/* Actions Column */}
                <div className="flex-shrink-0 pt-1 flex flex-col gap-3 items-center w-32">
                    {showTopActions && (
                        <div className="flex gap-1.5 pb-2 border-b border-slate-100 w-full justify-center mb-2">
                            {item.officialProcessLink && (
                                <a
                                    href={item.officialProcessLink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 hover:bg-teal-50 rounded-lg text-slate-400 hover:text-teal-700 cursor-pointer border border-transparent hover:border-teal-200 transition-all"
                                    title={t('manual.viewOfficialProcess')}
                                >
                                    <ExternalLink className="w-4 h-4" />
                                </a>
                            )}
                            {item.appLink && (
                                <button
                                    onClick={() => navigate(item.appLink)}
                                    className="p-2 hover:bg-teal-50 rounded-lg text-slate-400 hover:text-teal-700 cursor-pointer border border-transparent hover:border-teal-200 transition-all"
                                    title={t('manual.openAppLink')}
                                >
                                    <LinkIcon className="w-4 h-4" />
                                </button>
                            )}
                            {isManager && (
                                <>
                                    <button onClick={() => onEdit(item)} className="p-2 hover:bg-teal-50 rounded-lg text-slate-400 hover:text-teal-700 cursor-pointer border border-transparent hover:border-teal-200 transition-all" title={t('manual.edit')}>
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button onClick={() => onDelete(item._id)} className="p-2 hover:bg-red-50 rounded-lg text-slate-400 hover:text-red-500 cursor-pointer border border-transparent hover:border-red-200 transition-all" title={t('manual.delete')}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </>
                            )}
                        </div>
                    )}

                    <button
                        onClick={() => !item.isAcknowledged && onAcknowledge()}
                        disabled={item.isAcknowledged || ackPending}
                        className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all w-full ${item.isAcknowledged ? 'bg-emerald-50 border-emerald-300 text-emerald-700 cursor-default' : 'bg-white border-slate-200 hover:border-teal-500 text-slate-400 hover:text-teal-700 cursor-pointer hover:shadow-md hover:shadow-teal-700/10'}`}
                    >
                        {item.isAcknowledged ? <CheckCircle className="w-6 h-6" /> : <Circle className="w-6 h-6" />}
                        <span className="text-[10px] uppercase font-bold text-center leading-tight">
                            {item.isAcknowledged ? <Lang w="manual.acknowledged" /> : <Lang w="manual.markAsRead" />}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Maps section names to their lang.json translation key suffix */
const SECTION_TRANSLATION_MAP = {
    "General": "general",
    "Teacher": "teacher",
    "Part time teacher": "partTimeTeacher",
    "Foreign teacher": "foreignTeacher",
    "Teaching assistant": "teachingAssistant",
    "Marketing": "marketing",
    "Sales": "sales",
    "Customer care": "customerCare",
    "Academic assistant": "academicAssistant",
    "R & D Books": "rdBooks",
    "Manager": "manager"
};

/** Fixed list of section names in display order */
const FIXED_SECTIONS = [
    "General",
    "Teacher",
    "Part time teacher",
    "Foreign teacher",
    "Teaching assistant",
    "Marketing",
    "Sales",
    "Customer care",
    "Academic assistant",
    "R & D Books",
    "Manager"
];

/**
 * Operations Manual page component. Displays a bilingual operations manual
 * organized by sections and categories. Managers can create, edit, delete items
 * and manage categories. All users can acknowledge (mark as read) items.
 *
 * Uses drag-and-drop for category reordering via @dnd-kit.
 *
 * @date 4/7/2026
 * @version 1.0.0
 * @author Forge
 * @returns {React.ReactElement}
 */
export default function OperationsManualPage() {
    const { user } = useContext(UserContext);
    const { l, language } = useContext(LanguageContext);

    const [selectedSection, setSelectedSection] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isManagingCategories, setIsManagingCategories] = useState(false);
    const [formData, setFormData] = useState(DEFAULT_FORM);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Data State (via useDBO for auto-sync)
    const itemsDBO = useDBO('/manual', 60);
    const categoriesDBO = useDBO('/manual/categories', 60);
    const items = itemsDBO.get || [];
    const categories = categoriesDBO.get || [];
    const isLoading = itemsDBO.loading || categoriesDBO.loading;

    // Categories Management State
    const [newCategoryVi, setNewCategoryVi] = useState('');
    const [newCategoryEn, setNewCategoryEn] = useState('');
    const [activeCategory, setActiveCategory] = useState('');

    /**
     * Check if the current user has manager-level privileges.
     * @type {boolean}
     */
    const isManager = useMemo(() => {
        return (
          user?.roles?.includes("Human resources") ||
          user?.roles?.includes("Head Teacher")
        );
    }, [user]);

    /**
     * Helper to get a translation from lang.json.
     * @param {string} key - The translation key
     * @returns {string} The translated string for the current language
     */
    const t = (key) => {
        try {
            return l[key][language];
        } catch {
            return key;
        }
    };

    /**
     * Refreshes both manual items and categories from the API.
     * @date 4/7/2026
     * @version 2.0.0
     * @author opencode + kimi-k2.6
     */
    const refreshData = () => {
        itemsDBO.refresh();
        categoriesDBO.refresh();
    };

    /**
     * Acknowledges a manual item, marking it as read by the current user.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {string} itemId - The ID of the item to acknowledge
     * @returns {Promise<void>}
     */
    const acknowledgeItem = async (itemId) => {
        itemsDBO.updateById(itemId, { isAcknowledged: true });
        try {
            await net.post('/manual/acknowledge', { itemId });
        } catch (e) {
            console.error('Ack error', e);
            itemsDBO.updateById(itemId, { isAcknowledged: false });
        }
    };

    /**
     * Seeds/resets the manual data (development/admin action).
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @returns {Promise<void>}
     */
    const seedData = async () => {
        if (!window.confirm(t('manual.resetConfirm'))) return;
        try {
            await net.post('/manual/seed');
            refreshData();
            alert(t('manual.seeded'));
        } catch (e) {
            alert(t('manual.errorSeeding'));
        }
    };

    const sections = FIXED_SECTIONS;

    // Select first section by default
    useEffect(() => {
        if (!selectedSection) {
            setSelectedSection(sections[0]);
        }
    }, [selectedSection, sections]);

    // Dnd Sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    /**
     * Handles the end of a drag event for category reordering.
     * Performs an optimistic UI update and persists the new order via API.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {Object} event - The drag end event from @dnd-kit
     * @param {string} event.active.id - The ID of the dragged item
     * @param {Object} event.over - The drop target
     * @returns {Promise<void>}
     */
    const handleDragEnd = async (event) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            try {
                const oldIndex = categories.findIndex((item) => item._id === active.id);
                const newIndex = categories.findIndex((item) => item._id === over?.id);

                // Optimistic UI update
                const newCategories = arrayMove(categories, oldIndex, newIndex);
                categoriesDBO.set(newCategories);

                const updates = newCategories.map((cat, index) => ({
                    _id: cat._id,
                    order: index + 1
                }));

                await net.put('/manual/categories/reorder', { categories: updates });
            } catch (err) {
                console.error("Failed to reorder", err);
                refreshData(); // Revert on error
            }
        }
    };

    /** Categories filtered to the currently selected section */
    const currentSectionCategories = useMemo(() => {
        if (!categories || !selectedSection) return [];
        return categories.filter(c => c.section === selectedSection);
    }, [categories, selectedSection]);

    /** Items grouped by category ID for the current section */
    const groupedItems = useMemo(() => {
        if (!items || !selectedSection) return {};

        const sectionItems = items.filter(i => i.section === selectedSection);
        const groups = { 'Uncategorized': [] };

        // Initialize groups
        currentSectionCategories.forEach(c => {
            groups[c._id] = [];
        });

        sectionItems.forEach(item => {
            const catId = item.category || 'Uncategorized';
            if (!groups[catId]) groups[catId] = [];
            groups[catId].push(item);
        });

        return groups;
    }, [items, selectedSection, currentSectionCategories]);

    /** Acknowledgement progress statistics */
    const stats = useMemo(() => {
        if (!items) return { total: 0, acknowledged: 0, percent: 0 };
        const total = items.length;
        const acknowledged = items.filter(i => i.isAcknowledged).length;
        return {
            total,
            acknowledged,
            percent: total > 0 ? Math.round((acknowledged / total) * 100) : 0
        };
    }, [items]);

    /** Per-section unacknowledged item counts */
    const sectionUnreadCounts = useMemo(() => {
        if (!items) return {};
        return items.reduce((acc, item) => {
            if (!item.isAcknowledged) {
                acc[item.section] = (acc[item.section] || 0) + 1;
            }
            return acc;
        }, {});
    }, [items]);

    /**
     * Opens the edit modal in "create" mode for a given category.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {string} [categoryId] - Optional category ID to pre-assign
     */
    const handleCreate = (categoryId) => {
        setFormData({
            ...DEFAULT_FORM,
            section: selectedSection || 'General',
            category: categoryId || ''
        });
        setIsEditing(true);
    };

    /**
     * Opens the edit modal in "edit" mode, populating the form with existing item data.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {Object} item - The manual item to edit
     */
    const handleEdit = (item) => {
        setFormData({
            _id: item._id,
            title_vi: item.title_vi,
            title_en: item.title_en,
            content_vi: item.content_vi,
            content_en: item.content_en,
            section: item.section,
            category: item.category || '',
            roles: item.roles || [],
            officialProcessLink: item.officialProcessLink || '',
            appLink: item.appLink || '',
            videoUrl: item.videoUrl || '',
            imageUrls: item.imageUrls || []
        });
        setIsEditing(true);
    };

    /**
     * Deletes a manual item after user confirmation.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {string} id - The ID of the item to delete
     * @returns {Promise<void>}
     */
    const handleDelete = async (id) => {
        if (!window.confirm(t('manual.deleteConfirm'))) return;

        try {
            const res = await net.del(`/manual/items/${id}`);
            if (!res) throw new Error('Failed to delete');
            refreshData();
        } catch (error) {
            alert(t('manual.errorDeleting'));
        }
    };

    /**
     * Handles the form submission for creating or updating a manual item.
     * Uses POST for new items and PUT for existing items.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {React.FormEvent} e - The form submit event
     * @returns {Promise<void>}
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const url = formData._id ? `/manual/items/${formData._id}` : '/manual/items';
            const res = formData._id
                ? await net.put(url, formData)
                : await net.post(url, formData);
            if (!res) throw new Error('Failed to save');
            refreshData();
            setIsEditing(false);
        } catch (error) {
            alert(t('manual.errorSaving'));
        } finally {
            setIsSubmitting(false);
        }
    };

    /**
     * Creates a new category for the currently selected section.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {React.FormEvent} e - The form submit event
     * @returns {Promise<void>}
     */
    const handleCreateCategory = async (e) => {
        e.preventDefault();
        if (!newCategoryVi || !newCategoryEn) return;
        try {
            const res = await net.post('/manual/categories', {
                title_vi: newCategoryVi,
                title_en: newCategoryEn,
                section: selectedSection
            });
            if (!res) throw new Error('Failed to create category');
            refreshData();
            setNewCategoryVi('');
            setNewCategoryEn('');
        } catch (e) {
            alert(t('manual.errorCreatingCategory'));
        }
    };

    /**
     * Deletes a category after user confirmation.
     * @date 4/7/2026
     * @version 1.0.0
     * @author Forge
     * @param {string} id - The ID of the category to delete
     * @returns {Promise<void>}
     */
    const handleDeleteCategory = async (id) => {
        if (!window.confirm(t('manual.deleteCategoryConfirm'))) return;
        try {
            const res = await net.del(`/manual/categories/${id}`);
            if (!res) throw new Error('Failed to delete category');
            refreshData();
        } catch (e) {
            alert(t('manual.errorDeletingCategory'));
        }
    };

    return (
        <div className="h-[calc(100vh-8rem)] flex gap-6 relative">
            {/* Categories Management Modal */}
            {isManagingCategories && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl shadow-teal-900/20 overflow-hidden">
                        {/* Header */}
                        <div className="relative px-6 py-5 bg-gradient-to-r from-teal-700 to-teal-600">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-white">
                                        <Lang w="manual.manageCategories" />
                                    </h2>
                                    <p className="text-teal-200 text-sm mt-0.5">
                                        <Lang w={`manual.sections.${SECTION_TRANSLATION_MAP[selectedSection] || 'general'}`} />
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsManagingCategories(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-teal-100 hover:bg-white/20 hover:text-white cursor-pointer transition-all"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-5">
                            {/* Add Category Form */}
                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                                <form onSubmit={handleCreateCategory} className="flex gap-3 items-end">
                                    <div className="flex-1 space-y-2">
                                        <input
                                            placeholder={t('manual.vietnameseTitle')}
                                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={newCategoryVi}
                                            onChange={e => setNewCategoryVi(e.target.value)}
                                            required
                                        />
                                        <input
                                            placeholder={t('manual.englishTitle')}
                                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={newCategoryEn}
                                            onChange={e => setNewCategoryEn(e.target.value)}
                                            required
                                        />
                                    </div>
                                    <PremiumButton type="submit" size="md" leftIcon={<Plus className="w-4 h-4" />}><Lang w="manual.add" /></PremiumButton>
                                </form>
                            </div>

                            {/* Category List */}
                            <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                                {currentSectionCategories.length === 0 && (
                                    <div className="text-center py-8">
                                        <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-slate-400 text-sm"><Lang w="manual.noCategoriesYet" /></p>
                                    </div>
                                )}
                                {currentSectionCategories.map((cat, idx) => (
                                    <div key={cat._id} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-100 hover:border-teal-200 hover:shadow-sm transition-all group">
                                        <div className="flex items-center gap-3">
                                            <span className="w-6 h-6 flex items-center justify-center rounded-md bg-teal-50 text-teal-700 text-xs font-bold border border-teal-100">{idx + 1}</span>
                                            <div>
                                                <div className="font-semibold text-slate-800 text-sm">{cat.title_vi}</div>
                                                <div className="text-slate-400 text-xs">{cat.title_en}</div>
                                            </div>
                                        </div>
                                        <button onClick={() => handleDeleteCategory(cat._id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-all opacity-0 group-hover:opacity-100">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end">
                            <PremiumButton type="button" variant="ghost" onClick={() => setIsManagingCategories(false)}><Lang w="manual.cancel" /></PremiumButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {isEditing && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl shadow-teal-900/20 overflow-hidden max-h-[90vh] flex flex-col">
                        {/* Sticky Header */}
                        <div className="relative px-6 py-5 bg-gradient-to-r from-teal-700 to-teal-600 flex-shrink-0">
                            <div className="flex justify-between items-center">
                                <div>
                                    <h2 className="text-xl font-bold text-white">
                                        {formData._id ? <Lang w="manual.editItem" /> : <Lang w="manual.newItem" />}
                                    </h2>
                                    <p className="text-teal-200 text-sm mt-0.5">
                                        <Lang w={`manual.sections.${SECTION_TRANSLATION_MAP[formData.section] || 'general'}`} />
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsEditing(false)}
                                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 text-teal-100 hover:bg-white/20 hover:text-white cursor-pointer transition-all"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </div>

                        {/* Scrollable Body */}
                        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
                            <div className="p-6 space-y-5">
                                {/* Vietnamese content */}
                                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 space-y-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-1.5 h-5 bg-teal-700 rounded-full"></span>
                                        <span className="text-xs font-bold text-teal-700 uppercase tracking-wider">Tiếng Việt</span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5"><Lang w="manual.titleVi" /></label>
                                        <input
                                            type="text"
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={formData.title_vi}
                                            onChange={e => setFormData({ ...formData, title_vi: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5"><Lang w="manual.contentVi" /></label>
                                        <textarea
                                            rows={4}
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none resize-none transition-all shadow-sm"
                                            value={formData.content_vi}
                                            onChange={e => setFormData({ ...formData, content_vi: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* English content */}
                                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 space-y-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-1.5 h-5 bg-slate-400 rounded-full"></span>
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">English</span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5"><Lang w="manual.titleEn" /></label>
                                        <input
                                            type="text"
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={formData.title_en}
                                            onChange={e => setFormData({ ...formData, title_en: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5"><Lang w="manual.contentEn" /></label>
                                        <textarea
                                            rows={4}
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none resize-none transition-all shadow-sm"
                                            value={formData.content_en}
                                            onChange={e => setFormData({ ...formData, content_en: e.target.value })}
                                        />
                                    </div>
                                </div>

                                {/* Links & Media */}
                                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 space-y-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="w-1.5 h-5 bg-red-400 rounded-full"></span>
                                        <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{'Media & Links'}</span>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                                            <LinkIcon className="w-4 h-4 text-teal-600" />
                                            <Lang w="manual.officialProcessLink" />
                                        </label>
                                        <input
                                            type="url"
                                            placeholder="https://docs.google.com/..."
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={formData.officialProcessLink || ''}
                                            onChange={e => setFormData({ ...formData, officialProcessLink: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                                            <LinkIcon className="w-4 h-4 text-teal-600" />
                                            <Lang w="manual.appLink" />
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="/manage/leave-approvals"
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={formData.appLink || ''}
                                            onChange={e => setFormData({ ...formData, appLink: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                                            <Youtube className="w-4 h-4 text-red-500" />
                                            <Lang w="manual.youtubeLink" />
                                        </label>
                                        <input
                                            type="url"
                                            placeholder="https://youtube.com/watch?v=..."
                                            className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-slate-800 placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all shadow-sm"
                                            value={formData.videoUrl || ''}
                                            onChange={e => setFormData({ ...formData, videoUrl: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-2">
                                            <ImageIcon className="w-4 h-4 text-teal-600" />
                                            <Lang w="manual.images" />
                                        </label>

                                        <div className="flex flex-wrap gap-2 mb-3">
                                            {formData.imageUrls?.map((url, idx) => (
                                                <div key={idx} className="relative group w-20 h-20 rounded-lg overflow-hidden border border-slate-200 shadow-sm">
                                                    <img src={url} alt="Uploaded" className="w-full h-full object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormData({ ...formData, imageUrls: formData.imageUrls?.filter((_, i) => i !== idx) })}
                                                        className="absolute top-0 right-0 bg-red-500 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="relative">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                id="image-upload"
                                                onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;

                                                    const uploadData = new FormData();
                                                    uploadData.append('file', file);

                                                    try {
                                                        const res = await fetch('/api/upload', {
                                                            method: 'POST',
                                                            headers: { 'Authorization': `Bearer ${localStorage.getItem('apiKey')}` },
                                                            body: uploadData
                                                        });

                                                        if (res.ok) {
                                                            const data = await res.json();
                                                            setFormData(prev => ({ ...prev, imageUrls: [...(prev.imageUrls || []), data.url] }));
                                                        } else {
                                                            alert(t('manual.uploadFailed'));
                                                        }
                                                    } catch (err) {
                                                        console.error(err);
                                                        alert(t('manual.uploadFailed'));
                                                    }

                                                    // Reset input
                                                    e.target.value = '';
                                                }}
                                            />
                                            <PremiumButton
                                                type="button"
                                                size="sm"
                                                variant="secondary"
                                                onClick={() => document.getElementById('image-upload')?.click()}
                                                leftIcon={<Upload className="w-4 h-4" />}
                                            >
                                                <Lang w="manual.uploadImage" />
                                            </PremiumButton>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Sticky Footer */}
                            <div className="sticky bottom-0 px-6 py-4 border-t border-slate-200 bg-white flex justify-end gap-3">
                                <PremiumButton type="button" variant="ghost" onClick={() => setIsEditing(false)}><Lang w="manual.cancel" /></PremiumButton>
                                <PremiumButton type="submit" isLoading={isSubmitting} leftIcon={<Save className="w-4 h-4" />}>
                                    <Lang w="manual.saveItem" />
                                </PremiumButton>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Sidebar */}
            <div className="w-64 flex-shrink-0 flex flex-col gap-6">
                {/* Stats Card */}
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-md shadow-teal-700/5">
                    <h2 className="text-lg font-bold text-slate-800 mb-3 flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-teal-700" />
                        <Lang w="manual.progress" />
                    </h2>
                    <div className="relative pt-1">
                        <div className="flex mb-2 items-center justify-between">
                            <div>
                                <span className="text-xs font-semibold inline-block py-1 px-3 uppercase rounded-full text-teal-700 bg-teal-50 border border-teal-200">
                                    <Lang w="manual.read" />
                                </span>
                            </div>
                            <div className="text-right">
                                <span className="text-sm font-bold text-teal-700">
                                    {stats.percent}%
                                </span>
                            </div>
                        </div>
                        <div className="overflow-hidden h-2.5 mb-4 text-xs flex rounded-full bg-slate-100 border border-slate-200">
                            <div style={{ width: `${stats.percent}%` }} className="shadow-sm flex flex-col text-center whitespace-nowrap text-white justify-center bg-gradient-to-r from-teal-700 to-teal-500 transition-all duration-500 rounded-full"></div>
                        </div>
                        <div className="text-xs text-slate-400 text-center font-medium">
                            {stats.acknowledged} / {stats.total} <Lang w="manual.itemsAcknowledged" />
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <nav className="flex flex-col gap-1.5">
                    {sections.map(section => (
                        <button
                            key={section}
                            onClick={() => setSelectedSection(section)}
                            className={`flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium text-left transition-all duration-200 ${selectedSection === section
                                ? 'bg-teal-700 text-white shadow-lg shadow-teal-700/25 font-bold scale-[1.02] ring-2 ring-teal-600/30'
                                : 'bg-white text-slate-500 hover:bg-teal-50 hover:text-teal-700 hover:translate-x-1 border border-slate-200 hover:border-teal-300'
                                } cursor-pointer`}
                        >
                            <span>
                                <Lang w={`manual.sections.${SECTION_TRANSLATION_MAP[section] || 'general'}`} />
                            </span>
                            {(sectionUnreadCounts[section] || 0) > 0 && (
                                <span className={`ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold leading-none ${selectedSection === section ? 'bg-white text-red-500' : 'bg-red-500 text-white'}`}>
                                    {sectionUnreadCounts[section]}
                                </span>
                            )}
                        </button>
                    ))}
                </nav>

                {/* Manager Actions */}
                {isManager && (
                    <div className="mt-4 flex flex-col gap-2">
                        {/* Global Add Removed - Moved to Categories */}
                    </div>
                )}
            </div>

            {/* Main Content */}
            <div className="flex-1 bg-white rounded-2xl border border-slate-200 shadow-md shadow-teal-700/5 overflow-hidden flex flex-col relative">
                <div className="p-6 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800 tracking-tight"><Lang w="manual.operationsManual" /></h1>
                        <p className="text-slate-400 text-sm mt-0.5"><Lang w="manual.pleaseRead" /></p>
                    </div>
                </div>

                {/* Category Navigation (Horizontal Sticky) */}
                {!isLoading && (
                    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-6 py-3 flex gap-3 overflow-x-auto no-scrollbar items-center">
                        {groupedItems['Uncategorized']?.length > 0 && (
                            <button
                                onClick={() => document.getElementById('cat-uncategorized')?.scrollIntoView({ behavior: 'smooth' })}
                                className={`flex flex-col items-center justify-center whitespace-nowrap px-5 py-2 rounded-lg text-sm transition-all cursor-pointer ${activeCategory === 'Uncategorized' ? 'bg-teal-700 text-white shadow-lg shadow-teal-700/30' : 'bg-slate-50 text-slate-500 hover:text-teal-700 hover:bg-teal-50 border border-slate-200 hover:border-teal-300'}`}
                            >
                                <span className="font-bold"><Lang w="manual.general" /></span>
                                <span className={`text-[10px] uppercase tracking-wider ${activeCategory === 'Uncategorized' ? 'text-teal-100' : 'text-slate-400'}`}>General</span>
                            </button>
                        )}

                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                        >
                            <SortableContext
                                items={currentSectionCategories.map(c => c._id)}
                                strategy={horizontalListSortingStrategy}
                            >
                                {currentSectionCategories.map((cat) => {
                                    const hasItems = groupedItems[cat._id]?.length > 0;
                                    if (!hasItems && !isManager) return null;

                                    return (
                                        <SortableCategoryButton
                                            key={cat._id}
                                            category={cat}
                                            activeCategory={activeCategory}
                                            onClick={() => document.getElementById(`cat-${cat._id}`)?.scrollIntoView({ behavior: 'smooth' })}
                                        />
                                    );
                                })}
                            </SortableContext>
                        </DndContext>

                        {/* Edit Button for Managers */}
                        {isManager && (
                            <button
                                onClick={() => setIsManagingCategories(true)}
                                className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-50 text-slate-400 hover:bg-teal-700 hover:text-white transition-all border border-dashed border-slate-300 hover:border-teal-700 flex-shrink-0 cursor-pointer"
                                title={t('manual.manageCategories')}
                            >
                                <Edit2 className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                )}

                <div
                    className="overflow-y-auto p-6 space-y-12 flex-1 scroll-smooth"
                    onScroll={(e) => {
                        const container = e.currentTarget;
                        const headers = container.querySelectorAll('[data-category-header]');
                        let current = '';
                        headers.forEach((header) => {
                            const rect = header.getBoundingClientRect();
                            const containerRect = container.getBoundingClientRect();
                            if (rect.top - containerRect.top < 150) {
                                current = header.getAttribute('data-category-id') || '';
                            }
                        });
                        if (container.scrollTop < 50) {
                            if (groupedItems['Uncategorized']?.length > 0) current = 'Uncategorized';
                            else if (currentSectionCategories.length > 0) current = currentSectionCategories[0]._id;
                        }
                        if (current && current !== activeCategory) setActiveCategory(current);
                    }}
                >
                    {isLoading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-teal-700"></div>
                        </div>
                    ) : (
                        <>
                            {/* Render Uncategorized items FIRST */}
                            {(groupedItems['Uncategorized']?.length > 0 || isManager) && (
                                <div id="cat-uncategorized" data-category-header data-category-id="Uncategorized" className="space-y-4 scroll-mt-32">
                                    <div className="flex items-center justify-between pt-4 border-b border-slate-100 pb-2">
                                        <div className="flex items-center gap-2">
                                            <FolderOpen className="w-5 h-5 text-teal-700" />
                                            <h3 className="text-xl font-bold text-slate-800"><Lang w="manual.general" /></h3>
                                        </div>
                                        {isManager && (
                                            <PremiumButton onClick={() => handleCreate()} size="sm" variant="ghost" leftIcon={<Plus className="w-4 h-4" />}>
                                                <Lang w="manual.addItem" />
                                            </PremiumButton>
                                        )}
                                    </div>
                                    <div className="space-y-4">
                                        {groupedItems['Uncategorized'].map((item) => <ManualItemCard key={item._id} item={item} isManager={!!isManager} onEdit={handleEdit} onDelete={handleDelete} onAcknowledge={() => acknowledgeItem(item._id)} />)}
                                    </div>
                                </div>
                            )}

                            {/* Render items for each defined category */}
                            {currentSectionCategories.map((cat) => {
                                const catItems = groupedItems[cat._id] || [];
                                if (catItems.length === 0 && !isManager) return null;

                                return (
                                    <div key={cat._id} id={`cat-${cat._id}`} data-category-header data-category-id={cat._id} className="space-y-4 scroll-mt-32">
                                        <div className="flex items-center justify-between pt-4 border-b border-slate-100 pb-2">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <FolderOpen className="w-5 h-5 text-teal-700" />
                                                    <h3 className="text-xl font-bold text-slate-800">{cat.title_vi}</h3>
                                                </div>
                                                <h4 className="text-sm text-slate-400 pl-7">{cat.title_en}</h4>
                                            </div>
                                            {isManager && (
                                                <PremiumButton onClick={() => handleCreate(cat._id)} size="sm" variant="ghost" leftIcon={<Plus className="w-4 h-4" />}>
                                                    <Lang w="manual.addItem" />
                                                </PremiumButton>
                                            )}
                                        </div>
                                        <div className="space-y-4">
                                            {catItems.map((item) => <ManualItemCard key={item._id} item={item} isManager={!!isManager} onEdit={handleEdit} onDelete={handleDelete} onAcknowledge={() => acknowledgeItem(item._id)} />)}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* Empty State */}
                            {Object.values(groupedItems).flat().length === 0 && !isManager && (
                                <div className="text-center text-slate-400 py-12">
                                    <Lang w="manual.noItemsFound" />
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
